import type { LocationPing } from '@fdt/shared';
import { Box } from '@mui/material';
import L from 'leaflet';
import { useEffect, useRef } from 'react';

/**
 * Where each driver was when they last reported.
 *
 * Separate from `RouteMap` rather than another set of optional props on it:
 * that component is about one route's plan, this one is about several people at
 * one moment, and the only thing they share is Leaflet.
 *
 * The viewport is fitted **once**, on the first set of positions. Refitting on
 * every refresh would drag the map out from under whoever was reading it,
 * twenty seconds at a time.
 */
export function LiveMap({
  positions,
  tileUrl,
  height = 440,
}: {
  positions: LocationPing[];
  tileUrl: string;
  height?: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.LayerGroup | null>(null);
  const fittedRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return;
    }

    const map = L.map(containerRef.current).setView([45.1349, 7.7428], 11);
    L.tileLayer(tileUrl, {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    layersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    const timer = setTimeout(() => map.invalidateSize(), 250);

    return () => {
      clearTimeout(timer);
      map.remove();
      mapRef.current = null;
      layersRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layers = layersRef.current;
    if (!map || !layers) {
      return;
    }

    layers.clearLayers();

    for (const ping of positions) {
      const stale = Date.now() - Date.parse(ping.recordedAt) > 10 * 60_000;
      L.marker([ping.location.lat, ping.location.lng], {
        icon: driverIcon(ping.vehiclePlate ?? '?', stale),
      })
        .bindTooltip(`${ping.userName ?? '—'}${ping.vehiclePlate ? ` · ${ping.vehiclePlate}` : ''}`)
        .addTo(layers);

      // The accuracy circle is drawn because a position is a claim with a
      // margin, and a bare pin reads as certainty the data does not have.
      if (ping.accuracyMeters && ping.accuracyMeters > 20) {
        L.circle([ping.location.lat, ping.location.lng], {
          radius: ping.accuracyMeters,
          color: stale ? '#a8630a' : '#1b5e9c',
          weight: 1,
          opacity: 0.5,
          fillOpacity: 0.08,
        }).addTo(layers);
      }
    }

    if (!fittedRef.current && positions.length > 0) {
      const bounds = L.latLngBounds(
        positions.map((ping) => [ping.location.lat, ping.location.lng] as [number, number]),
      );
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [48, 48], maxZoom: 14 });
        fittedRef.current = true;
      }
    }
  }, [positions]);

  return (
    <Box
      ref={containerRef}
      sx={{
        height,
        borderRadius: 1,
        overflow: 'hidden',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        '& .leaflet-pane': { zIndex: 1 },
        '& .leaflet-control': { zIndex: 2 },
      }}
    />
  );
}

/** A plate on the map, dimmed when the position is no longer current. */
function driverIcon(plate: string, stale: boolean): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div style="
      background:${stale ? '#a8630a' : '#1b5e9c'};
      color:#fff;border:2px solid #fff;border-radius:4px;
      padding:2px 6px;white-space:nowrap;
      font:600 12px/16px system-ui;
      box-shadow:0 1px 4px rgba(0,0,0,.4);
      opacity:${stale ? 0.8 : 1};">${escapeHtml(plate)}</div>`,
    iconSize: [60, 20],
    iconAnchor: [30, 10],
  });
}

/** The plate comes from the database, so it is escaped rather than trusted. */
function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] ??
      character,
  );
}
